import { useMemo } from 'react';

import { Result, Separator, Status, VStack } from '@oaknorth/component-core-v2';
import { T } from '@oaknorth/util-intl';
import { useQuery } from '@oaknorth/util-rest';

import {
  ReleaseNoteDisplayCard,
  ReleaseNoteDisplayDivided,
  SKELETON_NOTE_CARD,
  SKELETON_NOTE_DIVIDED,
} from './ReleaseNoteDisplay';
import { ReleaseNote } from './types';

export interface ReleaseNotesDisplayProps {
  id: string;
  getContent: () => Promise<ReleaseNote[]>;
  variant?: 'card' | 'divided';
}

export function ReleaseNotesDisplay({ getContent, id, variant = 'card' }: ReleaseNotesDisplayProps) {
  // eslint-disable-next-line @oaknorth/ui/no-template-literals -- queryKey, not user-visible
  const getNotes = useQuery({ queryKey: [`release-note-${id}`], queryFn: getContent });
  const notes = getNotes.data;
  const sortedNotes = useMemo(() => notes?.slice().sort((a, b) => b.date.localeCompare(a.date)), [notes]);

  if (getNotes.isError) {
    return <Result status={Status.ERROR} title={T`releaseNotes.loadingError`} />;
  }

  const skeletonNote = variant === 'card' ? SKELETON_NOTE_CARD : SKELETON_NOTE_DIVIDED;
  return (
    <VStack
      gap={8}
      align="stretch"
      separator={variant === 'divided' ? <Separator borderColor="gray.400" mt={8} mb={8} /> : undefined}
    >
      {getNotes.isPending ? (
        <>
          {skeletonNote}
          {skeletonNote}
        </>
      ) : sortedNotes?.length ? (
        sortedNotes.map((note, index) =>
          variant === 'card' ? (
            <ReleaseNoteDisplayCard key={index} note={note} />
          ) : (
            <ReleaseNoteDisplayDivided key={index} note={note} />
          )
        )
      ) : (
        <Result status={Status.INFO} title={T`releaseNotes.empty.title`} text={T`releaseNotes.empty.text`} />
      )}
    </VStack>
  );
}
